import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
  useCallback,
} from 'react';
import {
  clearAuthSession as clearAuthSessionUtil,
  isAuthenticated as isAuthenticatedUtil,
  saveAuthSession as saveAuthSessionUtil,
  AuthSession,
  AuthUser,
  getAuthToken,
  getAuthSession,
} from 'features/auth/authSession';
import { logout as logoutApi } from 'features/auth/authApi';

interface AuthContextInterface {
  isAuthenticated: boolean;
  user: AuthUser | null;
  login: (session: AuthSession) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextInterface | undefined>(undefined);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    isAuthenticatedUtil()
  );

    const [user, setUser] = useState<AuthUser | null>(() => {
    const session = getAuthSession();
    return session?.user || null;
  });

  const login = useCallback((session: AuthSession) => {
    saveAuthSessionUtil(session);
    setIsAuthenticated(true);
    setUser(session.user); 
  }, []);

  const logout = useCallback(async () => {
    try {
      const token = getAuthToken();
      await logoutApi(token);
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      clearAuthSessionUtil();
      setIsAuthenticated(false);
      setUser(null);
    }
  }, []);

  const value: AuthContextInterface = {
    isAuthenticated,
    user,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
